import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { AlertTriangle, UserCog, Check, X, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * AttributionCard — surfaces speaker-vs-registered agent mismatches.
 *
 * Two states:
 *   1. Auto-reassigned banner (info) — the seat-swap detector already
 *      moved the call; shows what changed + a "Revert" link.
 *   2. Open flag banner (warning) — regex detected a mismatch but the
 *      pattern hasn't hit the seat-swap threshold yet. Offers a
 *      one-click "Reassign to X" and a "Keep as is" dismiss.
 *
 * Also renders a subtle "Reassign agent…" link (item G from the plan)
 * so a manager can move any call to any agent regardless of flag —
 * useful for legacy cleanup.
 */
const AttributionCard = ({ call, onReassigned }) => {
  const [state, setState] = useState(null);
  const [busy, setBusy] = useState(false);
  const [manualOpen, setManualOpen] = useState(false);
  const [directory, setDirectory] = useState([]);
  const [manualExt, setManualExt] = useState('');
  const [manualReason, setManualReason] = useState('');

  useEffect(() => {
    if (!call?.call_id) return;
    axios.get(`${API}/calls/${call.call_id}/attribution-flag`, { timeout: 12000 })
      .then((r) => setState(r.data))
      .catch(() => setState(null));
  }, [call?.call_id]);

  const loadDirectory = () => {
    if (directory.length > 0) return;
    axios.get(`${API}/users/directory`, { timeout: 12000 })
      .then((r) => setDirectory(r.data?.users || []))
      .catch(() => setDirectory([]));
  };

  const applyReassign = async (extension, reason = '') => {
    setBusy(true);
    try {
      const r = await axios.post(
        `${API}/calls/${call.call_id}/reassign-agent`,
        { agent_id: extension, reason },
        { timeout: 15000 },
      );
      toast.success(`Reassigned to ${r.data.agent_name} (ext ${r.data.agent_id})`);
      setState((s) => ({ ...(s || {}), flag: null }));
      onReassigned?.(r.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Reassignment failed');
    } finally {
      setBusy(false);
      setManualOpen(false);
      setManualExt('');
      setManualReason('');
    }
  };

  const dismissFlag = async () => {
    if (!call?.call_id) return;
    setBusy(true);
    try {
      await axios.post(`${API}/admin/attribution-flags/${call.call_id}/dismiss`, {}, { timeout: 12000 });
      setState((s) => ({ ...(s || {}), flag: null }));
      toast.success('Flag dismissed');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to dismiss');
    } finally {
      setBusy(false);
    }
  };

  if (!state) return null;

  const flag = state.flag;
  const wasAutoReassigned = state.auto_reassigned;

  // Nothing to show + no manual affordance yet — render the tiny
  // "Reassign agent…" link on its own.
  if (!flag && !wasAutoReassigned) {
    return (
      <div className="flex justify-end -mb-2">
        <button
          type="button"
          onClick={() => { setManualOpen((v) => !v); loadDirectory(); }}
          className="text-[11px] text-slate-500 hover:text-slate-700 underline underline-offset-2 flex items-center gap-1"
          data-testid="attribution-manual-toggle"
        >
          <UserCog className="w-3 h-3" />
          {manualOpen ? 'Cancel' : 'Reassign agent…'}
        </button>
        {manualOpen && (
          <ManualReassignForm
            directory={directory}
            currentExt={String(call?.agent_id || '')}
            manualExt={manualExt} setManualExt={setManualExt}
            manualReason={manualReason} setManualReason={setManualReason}
            busy={busy}
            onSubmit={() => applyReassign(manualExt, manualReason)}
          />
        )}
      </div>
    );
  }

  return (
    <>
      {wasAutoReassigned && (
        <div
          className="rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-2.5 flex items-start gap-3 mb-3"
          data-testid="attribution-auto-reassigned"
        >
          <Check className="w-4 h-4 text-emerald-600 flex-shrink-0 mt-0.5" />
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-emerald-900">
              Auto-reassigned to {call.agent_name} (ext {call.agent_id})
            </p>
            <p className="text-[11px] text-emerald-700">
              Seat swap detected — this extension has been answered by this agent 3+ times in the last 30 days. Was assigned to{' '}
              {state.reassigned_from?.agent_name} (ext {state.reassigned_from?.agent_id}).
            </p>
          </div>
        </div>
      )}
      {flag && (
        <div
          className="rounded-lg border border-amber-300 bg-amber-50 px-4 py-3 flex items-start gap-3 mb-3"
          data-testid="attribution-flag-banner"
        >
          <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-amber-900">
              Speaker mismatch — transcript says &ldquo;{flag.detected_name}&rdquo; but this call is on{' '}
              {flag.registered_agent_name || `ext ${flag.registered_extension}`}&rsquo;s extension.
            </p>
            {flag.suggested_agent_name ? (
              <p className="text-[12px] text-amber-800 mt-0.5">
                Suggested: reassign to {flag.suggested_agent_name} (ext {flag.suggested_agent_id}).
              </p>
            ) : (
              <p className="text-[12px] text-amber-800 mt-0.5">
                No matching user found in your directory. Use &ldquo;Reassign agent…&rdquo; below to move manually.
              </p>
            )}
            <div className="flex flex-wrap items-center gap-2 mt-2">
              {flag.suggested_agent_id && (
                <button
                  type="button"
                  onClick={() => applyReassign(flag.suggested_agent_id, `Attribution flag — spoken name ${flag.detected_name}`)}
                  disabled={busy}
                  className="inline-flex items-center gap-1.5 text-xs font-semibold text-white bg-amber-600 hover:bg-amber-700 disabled:opacity-50 px-2.5 py-1 rounded"
                  data-testid="attribution-reassign-suggested"
                >
                  {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <UserCog className="w-3 h-3" />}
                  Reassign to {flag.suggested_agent_name}
                </button>
              )}
              <button
                type="button"
                onClick={() => { setManualOpen((v) => !v); loadDirectory(); }}
                className="inline-flex items-center gap-1 text-xs font-medium text-amber-800 hover:text-amber-900 underline underline-offset-2"
                data-testid="attribution-reassign-other"
              >
                <UserCog className="w-3 h-3" /> Reassign to someone else…
              </button>
              <button
                type="button"
                onClick={dismissFlag}
                disabled={busy}
                className="inline-flex items-center gap-1 text-xs font-medium text-slate-600 hover:text-slate-800"
                data-testid="attribution-dismiss"
              >
                <X className="w-3 h-3" /> Keep as {flag.registered_agent_name || 'is'}
              </button>
            </div>
            {manualOpen && (
              <ManualReassignForm
                directory={directory}
                currentExt={String(call?.agent_id || '')}
                manualExt={manualExt} setManualExt={setManualExt}
                manualReason={manualReason} setManualReason={setManualReason}
                busy={busy}
                onSubmit={() => applyReassign(manualExt, manualReason)}
              />
            )}
          </div>
        </div>
      )}
    </>
  );
};

const ManualReassignForm = ({
  directory, currentExt, manualExt, setManualExt,
  manualReason, setManualReason, busy, onSubmit,
}) => (
  <div
    className="mt-2 flex flex-wrap items-center gap-2 bg-white border border-slate-200 rounded-md p-2"
    data-testid="attribution-manual-form"
  >
    <select
      value={manualExt}
      onChange={(e) => setManualExt(e.target.value)}
      className="text-xs border border-slate-200 rounded px-2 py-1 focus:outline-none focus:ring-1 focus:ring-teal-400 max-w-[220px]"
      data-testid="attribution-manual-select"
    >
      <option value="">Choose agent…</option>
      {directory
        .filter((u) => String(u.extension) && String(u.extension) !== currentExt)
        .map((u) => (
          <option key={u.email} value={u.extension}>
            {u.name || u.email} (ext {u.extension})
          </option>
        ))}
    </select>
    <input
      type="text"
      value={manualReason}
      onChange={(e) => setManualReason(e.target.value)}
      placeholder="Reason (optional)"
      maxLength={200}
      className="flex-1 min-w-[160px] text-xs border border-slate-200 rounded px-2 py-1 focus:outline-none focus:ring-1 focus:ring-teal-400"
      data-testid="attribution-manual-reason"
    />
    <button
      type="button"
      onClick={onSubmit}
      disabled={busy || !manualExt}
      className="text-xs font-semibold text-white bg-teal-600 hover:bg-teal-700 disabled:opacity-50 px-3 py-1 rounded"
      data-testid="attribution-manual-submit"
    >
      {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Reassign'}
    </button>
  </div>
);

export default AttributionCard;
